(function () {
  'use strict';

  const MAX_SLOTS = 7;

  // ─── State ────────────────────────────────────────────────────────────────
  const state = {
    allProducts: [],
    filteredProducts: [],
    selectedItems: [],   // {productId, variantId, title, vendor, price, image, variantTitle}
    currentPage: 1,
    perPage: 24,
    activeCollection: '',
    searchQuery: '',
    isLoading: false,
    isFetched: false
  };

  let config = {};
  let searchDebounceTimer = null;

  // ─── Init ─────────────────────────────────────────────────────────────────
  function init() {
    const configEl = document.getElementById('giftboxConfig');
    if (!configEl) return;

    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('Giftbox config parse error:', e);
      return;
    }

    state.perPage = config.perPage || 24;

    // Load filter collection sets
    const fcEl = document.getElementById('giftboxFilterCollections');
    if (fcEl) {
      try {
        const fcs = JSON.parse(fcEl.textContent);
        config.filterCollectionSets = {};
        fcs.forEach(fc => {
          config.filterCollectionSets[fc.handle] = new Set(fc.productIds);
        });
      } catch (e) { config.filterCollectionSets = {}; }
    }

    setupEventListeners();
    setupMobileKeyboardDismiss();
    loadProducts();
    updateStickyBar();
    observeStickyVisibility();
  }

  // ─── Product Loading ───────────────────────────────────────────────────────
  async function loadProducts() {
    const cached = getCache(config.cacheKey);
    if (cached && cached.length > 0) {
      state.allProducts = cached;
      state.isFetched = true;
      applyFilters();
      return;
    }

    setLoading(true);

    const mainHandle = config.mainCollectionHandle;
    if (!mainHandle) {
      showError('No collection configured. Please set one in the theme editor.');
      setLoading(false);
      return;
    }

    const productMap = new Map();

    let page = 1;
    while (true) {
      try {
        const res = await fetch(`/collections/${mainHandle}/products.json?limit=250&page=${page}`);
        if (!res.ok) break;
        const data = await res.json();
        const products = data.products || [];
        if (!products.length) break;

        products.forEach(p => {
          if (!productMap.has(p.id)) {
            productMap.set(p.id, normalizeProduct(p));
          }
        });

        if (products.length < 250) break;
        page++;
      } catch (err) {
        console.error('Fetch error:', err);
        break;
      }
    }

    state.allProducts = Array.from(productMap.values());
    state.isFetched = true;
    setCache(config.cacheKey, state.allProducts);
    setLoading(false);
    applyFilters();
  }

  function normalizeProduct(p) {
    const img = p.images && p.images.length ? p.images[0].src : '';
    return {
      id: p.id,
      title: p.title,
      vendor: p.vendor || '',
      handle: p.handle,
      price: p.variants[0]?.price || 0,
      compare_at_price: p.variants[0]?.compare_at_price || 0,
      image: img ? img.replace(/(\.[^.]+)$/, '_medium$1') : '',
      has_only_default_variant: p.variants.length === 1 && p.variants[0].title === 'Default Title',
      options: p.options || [],
      variants: (p.variants || []).map(v => ({
        id: v.id,
        title: v.title,
        price: v.price,
        compare_at_price: v.compare_at_price,
        available: v.available,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        featured_image: v.featured_image ? v.featured_image.src : null
      }))
    };
  }

  // ─── Filtering & Search ────────────────────────────────────────────────────
  function applyFilters() {
    const q = state.searchQuery.toLowerCase().trim();
    const col = state.activeCollection;
    const colSet = col && config.filterCollectionSets ? config.filterCollectionSets[col] : null;

    state.filteredProducts = state.allProducts.filter(p => {
      const matchCollection = !col || (colSet && colSet.has(p.id));
      const matchSearch = !q || p.title.toLowerCase().includes(q) || p.vendor.toLowerCase().includes(q);
      return matchCollection && matchSearch;
    });

    state.currentPage = 1;
    renderGrid();
    renderPagination();
    updateResultsCount();
  }

  // ─── Rendering ────────────────────────────────────────────────────────────
  function renderGrid() {
    const skeleton = document.getElementById('giftboxSkeletonGrid');
    if (skeleton) skeleton.style.display = 'none';

    const container = document.getElementById('giftboxProductsContainer');
    if (!container) return;

    const start = (state.currentPage - 1) * state.perPage;
    const page = state.filteredProducts.slice(start, start + state.perPage);

    if (!page.length) {
      container.innerHTML = `<div class="giftbox-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <p>No products found. Try a different search or filter.</p>
      </div>`;
      return;
    }

    container.innerHTML = page.map(p => renderProductCard(p)).join('');

    // Restore selected state and max-reached dimming
    const maxReached = state.selectedItems.length >= MAX_SLOTS;
    state.selectedItems.forEach(item => {
      const card = container.querySelector(`[data-product-id="${item.productId}"]`);
      if (card) {
        card.classList.add('selected');
        const slotNum = state.selectedItems.findIndex(i => i.productId === item.productId) + 1;
        const slotBadge = card.querySelector('.gb-slot-badge');
        if (slotBadge) slotBadge.textContent = slotNum;
        syncVariantSelectToItem(card, item);
      }
    });

    if (maxReached) {
      container.querySelectorAll('.giftbox-product-card:not(.selected)').forEach(card => {
        card.classList.add('max-reached');
      });
    }

    attachCardListeners(container);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function renderProductCard(product) {
    const selectedItem = state.selectedItems.find(i => i.productId == product.id);
    const isSelected = !!selectedItem;
    const slotNum = isSelected ? state.selectedItems.findIndex(i => i.productId == product.id) + 1 : '';
    const maxReached = state.selectedItems.length >= MAX_SLOTS;

    const firstNonExcluded = product.variants.find(v =>
      !['option1', 'option2', 'option3'].some(opt => v[opt] && isExcludedValue(v[opt])) && v.available
    ) || product.variants.find(v =>
      !['option1', 'option2', 'option3'].some(opt => v[opt] && isExcludedValue(v[opt]))
    ) || product.variants[0];

    const currentVariantId = selectedItem ? selectedItem.variantId : firstNonExcluded.id;
    const currentVariant = product.variants.find(v => v.id == currentVariantId) || firstNonExcluded;

    const price = parseFloat(currentVariant.price) || 0;
    const comparePrice = parseFloat(currentVariant.compare_at_price) || 0;
    const hasDiscount = comparePrice > price;
    const discountPct = hasDiscount ? Math.round(((comparePrice - price) / comparePrice) * 100) : 0;

    const variantOptions = product.has_only_default_variant ? '' : product.options.map((opt, i) => {
      let values;
      if (i === 0) {
        values = [...new Set(product.variants.map(v => v['option1']).filter(Boolean))];
      } else {
        const prevSelections = product.options.slice(0, i).map((_, j) => currentVariant[`option${j + 1}`]);
        const matching = product.variants.filter(v =>
          prevSelections.every((val, j) => v[`option${j + 1}`] === val)
        );
        values = [...new Set(matching.map(v => v[`option${i + 1}`]).filter(Boolean))];
      }
      values = values.filter(val => !isExcludedValue(val));
      const labelHtml = opt.name === 'Quantity'
        ? `<span class="gb-quantity-label">${opt.name}:</span>`
        : `${escapeHtml(opt.name)}:`;

      return `<div class="gb-variant-option">
        <label>${labelHtml}</label>
        <select class="gb-variant-select" data-option-index="${i}" data-option-name="${escapeAttr(opt.name)}" data-product-id="${product.id}">
          ${values.map(val => `<option value="${escapeAttr(val)}" ${currentVariant[`option${i + 1}`] === val ? 'selected' : ''}>${escapeHtml(val)}</option>`).join('')}
        </select>
      </div>`;
    }).join('');

    const cardClasses = [
      'giftbox-product-card',
      isSelected ? 'selected' : '',
      (!isSelected && maxReached) ? 'max-reached' : ''
    ].filter(Boolean).join(' ');

    return `<div class="${cardClasses}"
        data-product-id="${product.id}"
        data-selected-variant-id="${currentVariantId}"
        data-product-title="${escapeAttr(product.title)}"
        data-product-vendor="${escapeAttr(product.vendor)}"
        data-product-image="${escapeAttr(product.image)}"
        data-variants='${escapeJson(product.variants)}'>

      <div class="gb-selected-indicator">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="11" fill="#000" stroke="white" stroke-width="1.5"/>
          <path d="M7 12L10.5 15.5L17 9" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      <div class="gb-slot-badge">${slotNum}</div>

      ${hasDiscount && !isSelected ? `<div class="gb-card-discount-badge">-${discountPct}%</div>` : ''}

      <div class="gb-product-image-wrapper">
        <img src="${product.image || ''}"
             alt="${escapeAttr(product.title)}"
             loading="lazy"
             class="gb-product-img">
      </div>

      <div class="gb-product-info">
        <div class="gb-product-vendor">${escapeHtml(product.vendor)}</div>
        <h3 class="gb-product-title">${escapeHtml(product.title)}</h3>

        ${variantOptions ? `<div class="gb-variant-selector-wrapper">${variantOptions}</div>` : ''}

        <div class="gb-product-price">
          ${hasDiscount
            ? `<span class="gb-price-sale">AED ${Math.round(price)}</span>
               <span class="gb-price-compare">AED ${Math.round(comparePrice)}</span>`
            : `<span class="gb-price-sale">AED ${Math.round(price)}</span>`
          }
        </div>
      </div>

      <button type="button" class="gb-select-btn">
        <span class="gb-btn-text-select">+ Add to Box</span>
        <span class="gb-btn-text-selected">✓ Added</span>
      </button>
    </div>`;
  }

  function renderPagination() {
    const container = document.getElementById('giftboxPagination');
    if (!container) return;

    const totalPages = Math.ceil(state.filteredProducts.length / state.perPage);
    if (totalPages <= 1) { container.innerHTML = ''; return; }

    const cur = state.currentPage;
    let pages = [];
    pages.push(1);
    if (cur > 3) pages.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(totalPages - 1, cur + 1); i++) pages.push(i);
    if (cur < totalPages - 2) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);

    container.innerHTML = `
      <button class="gb-page-btn" data-page="${cur - 1}" ${cur === 1 ? 'disabled' : ''}>← Prev</button>
      ${pages.map(p => p === '...'
        ? `<span class="gb-page-ellipsis">…</span>`
        : `<button class="gb-page-btn${p === cur ? ' active' : ''}" data-page="${p}">${p}</button>`
      ).join('')}
      <button class="gb-page-btn" data-page="${cur + 1}" ${cur === totalPages ? 'disabled' : ''}>Next →</button>`;

    container.querySelectorAll('.gb-page-btn:not([disabled])').forEach(btn => {
      btn.addEventListener('click', () => {
        state.currentPage = parseInt(btn.dataset.page);
        renderGrid();
        renderPagination();
      });
    });
  }

  function updateResultsCount() {
    const el = document.getElementById('giftboxResultsCount');
    if (!el) return;
    const total = state.filteredProducts.length;
    const start = (state.currentPage - 1) * state.perPage + 1;
    const end = Math.min(state.currentPage * state.perPage, total);
    el.textContent = total === 0
      ? 'No products found'
      : `Showing ${start}–${end} of ${total} products`;
  }

  // ─── Card Listeners ────────────────────────────────────────────────────────
  function attachCardListeners(container) {
    container.querySelectorAll('.giftbox-product-card').forEach(card => {
      card.querySelector('.gb-select-btn')?.addEventListener('click', e => {
        e.stopPropagation();
        toggleSelection(card);
      });

      card.querySelectorAll('.gb-variant-select').forEach((select, idx) => {
        select.addEventListener('change', e => {
          e.stopPropagation();
          handleVariantChange(card, select, idx);
        });
        select.addEventListener('click', e => e.stopPropagation());
      });

      card.addEventListener('click', e => {
        if (!e.target.closest('.gb-variant-select') && !e.target.closest('.gb-select-btn')) {
          toggleSelection(card);
        }
      });
    });
  }

  function handleVariantChange(card, changedSelect, changedIndex) {
    const productId = card.dataset.productId;
    const variants = JSON.parse(card.dataset.variants || '[]');
    const allSelects = card.querySelectorAll('.gb-variant-select');

    updateDependentDropdowns(card, variants, allSelects, changedIndex);
    updateCustomLabels(card, allSelects);

    const selectedOptions = Array.from(allSelects).map(s => s.value);
    const matched = variants.find(v =>
      selectedOptions.every((opt, i) => v[`option${i + 1}`] === opt)
    ) || variants[0];

    card.dataset.selectedVariantId = matched.id;

    const price = parseFloat(matched.price) || 0;
    const compare = parseFloat(matched.compare_at_price) || 0;
    const hasDiscount = compare > price;

    const priceEl = card.querySelector('.gb-product-price');
    if (priceEl) {
      priceEl.innerHTML = hasDiscount
        ? `<span class="gb-price-sale">AED ${Math.round(price)}</span><span class="gb-price-compare">AED ${Math.round(compare)}</span>`
        : `<span class="gb-price-sale">AED ${Math.round(price)}</span>`;
    }

    if (matched.featured_image) {
      const img = card.querySelector('.gb-product-img');
      if (img) {
        img.style.opacity = '0.5';
        img.src = matched.featured_image;
        img.onload = () => { img.style.opacity = '1'; };
      }
    }

    const idx = state.selectedItems.findIndex(i => i.productId == productId);
    if (idx !== -1) {
      state.selectedItems[idx].variantId = matched.id;
      state.selectedItems[idx].price = parseFloat(matched.price) || 0;
      state.selectedItems[idx].variantTitle = matched.title !== 'Default Title' ? matched.title : '';
      if (matched.featured_image) state.selectedItems[idx].image = matched.featured_image;
      updateStickyBar();
    }
  }

  function updateDependentDropdowns(card, variants, allSelects, changedIndex) {
    for (let nextIdx = changedIndex + 1; nextIdx < allSelects.length; nextIdx++) {
      const nextSelect = allSelects[nextIdx];
      const prevSelections = Array.from(allSelects).slice(0, nextIdx).map(s => s.value);
      const matching = variants.filter(v =>
        prevSelections.every((val, j) => v[`option${j + 1}`] === val)
      );
      const seen = new Set();
      const availValues = [];
      matching.forEach(v => {
        const val = v[`option${nextIdx + 1}`];
        if (val && !seen.has(val)) {
          seen.add(val);
          const hasAvail = matching.some(mv => mv[`option${nextIdx + 1}`] === val && mv.available);
          availValues.push({ val, hasAvail });
        }
      });
      const filteredValues = availValues.filter(({ val }) => !isExcludedValue(val));
      const currentVal = nextSelect.value;
      nextSelect.innerHTML = filteredValues.map(({ val, hasAvail }) =>
        `<option value="${escapeAttr(val)}"${!hasAvail ? ' disabled' : ''}>${escapeHtml(val)}${!hasAvail ? ' (Out of Stock)' : ''}</option>`
      ).join('');
      if (filteredValues.some(fv => fv.val === currentVal)) nextSelect.value = currentVal;
    }
  }

  function updateCustomLabels(card, allSelects) {
    allSelects.forEach(select => {
      if (select.dataset.optionName === 'Find the Fragrance') {
        const fragranceType = select.value || '';
        const quantityLabel = card.querySelector('.gb-quantity-label');
        if (quantityLabel) {
          quantityLabel.textContent = fragranceType.toLowerCase().includes('travel spray')
            ? 'Color : 10 ML'
            : 'Quantity:';
        }
      }
    });
  }

  function syncVariantSelectToItem(card, item) {
    const variants = JSON.parse(card.dataset.variants || '[]');
    const variant = variants.find(v => v.id == item.variantId);
    if (!variant) return;
    card.querySelectorAll('.gb-variant-select').forEach((sel, i) => {
      const val = variant[`option${i + 1}`];
      if (val) sel.value = val;
    });
  }

  // ─── Selection ─────────────────────────────────────────────────────────────
  function toggleSelection(card) {
    const isSelected = card.classList.contains('selected');

    if (isSelected) {
      removeFromSelection(card);
    } else {
      if (state.selectedItems.length >= MAX_SLOTS) {
        showToast(`You can only select ${MAX_SLOTS} travel sprays for this gift box.`, 'info');
        return;
      }
      addToSelection(card);
    }
    updateStickyBar();
    // Re-apply max-reached dimming to all visible cards
    refreshMaxReachedState();
  }

  function addToSelection(card) {
    const productId = card.dataset.productId;
    if (state.selectedItems.find(i => i.productId == productId)) return;

    const variantId = card.dataset.selectedVariantId;
    const variants = JSON.parse(card.dataset.variants || '[]');
    const variant = variants.find(v => v.id == variantId) || variants[0];

    state.selectedItems.push({
      productId: parseInt(productId),
      variantId: variant ? variant.id : parseInt(productId),
      title: card.dataset.productTitle,
      vendor: card.dataset.productVendor,
      price: variant ? parseFloat(variant.price) : 0,
      image: (variant && variant.featured_image) ? variant.featured_image : card.dataset.productImage,
      variantTitle: (variant && variant.title !== 'Default Title') ? variant.title : ''
    });

    card.classList.add('selected');

    // Update slot badge
    const slotNum = state.selectedItems.length;
    const slotBadge = card.querySelector('.gb-slot-badge');
    if (slotBadge) slotBadge.textContent = slotNum;
  }

  function removeFromSelection(card) {
    const productId = card.dataset.productId;
    state.selectedItems = state.selectedItems.filter(i => i.productId != productId);
    card.classList.remove('selected');

    // Re-number remaining slots
    refreshSlotBadges();
  }

  function refreshSlotBadges() {
    state.selectedItems.forEach((item, idx) => {
      const card = document.querySelector(`[data-product-id="${item.productId}"]`);
      if (card) {
        const badge = card.querySelector('.gb-slot-badge');
        if (badge) badge.textContent = idx + 1;
      }
    });
  }

  function refreshMaxReachedState() {
    const maxReached = state.selectedItems.length >= MAX_SLOTS;
    document.querySelectorAll('.giftbox-product-card').forEach(card => {
      if (!card.classList.contains('selected')) {
        card.classList.toggle('max-reached', maxReached);
      }
    });
  }

  // ─── Sticky Bar ────────────────────────────────────────────────────────────
  function updateStickyBar() {
    const count = state.selectedItems.length;
    const remaining = MAX_SLOTS - count;
    const isComplete = count === MAX_SLOTS;

    // Count label
    gbEl('giftboxStickyCount', count);

    // Complete badge
    const completeBadge = document.getElementById('giftboxCompleteBadge');
    if (completeBadge) {
      completeBadge.classList.toggle('visible', isComplete);
    }

    // Thumbnails
    renderThumbnails();

    // Progress dots (7)
    const dots = document.querySelectorAll('.gb-progress-dot');
    dots.forEach((dot, i) => {
      dot.classList.remove('filled', 'pulse');
      if (i < count) {
        dot.classList.add('filled');
      } else if (i === count && count < MAX_SLOTS) {
        dot.classList.add('pulse');
      }
    });

    // Progress text
    const progressText = document.getElementById('giftboxProgressText');
    if (progressText) {
      if (count === 0) {
        progressText.textContent = `Select ${MAX_SLOTS} travel sprays for your gift box`;
      } else if (isComplete) {
        progressText.innerHTML = `🎁 <strong>Gift Box Complete!</strong> Ready to add to cart`;
      } else {
        progressText.innerHTML = `<strong>${remaining}</strong> more to complete your gift box`;
      }
    }

    // Fixed gift box price — use config.giftBoxPrice if set, otherwise sum of selected
    const totalPrice = config.giftBoxPrice
      ? parseFloat(config.giftBoxPrice)
      : state.selectedItems.reduce((s, i) => s + i.price, 0);

    const totalEl = document.getElementById('giftboxTotalPrice');
    if (totalEl) totalEl.textContent = `AED ${Math.round(totalPrice)}`;

    const fixedLabel = document.getElementById('giftboxFixedLabel');
    if (fixedLabel) {
      fixedLabel.style.display = config.giftBoxPrice ? 'inline' : 'none';
    }

    // CTA button
    const btn = document.getElementById('giftboxCtaBtn');
    const btnText = document.getElementById('giftboxCtaText');
    if (btn && btnText) {
      if (count === 0) {
        btn.disabled = true;
        btn.classList.remove('ready-mode');
        btnText.textContent = 'Select your travel sprays';
      } else if (isComplete) {
        btn.disabled = false;
        btn.classList.add('ready-mode');
        btnText.textContent = `🎁 Add Gift Box — AED ${Math.round(totalPrice)}`;
      } else {
        btn.disabled = true;
        btn.classList.remove('ready-mode');
        btnText.textContent = `Add to Cart — AED ${Math.round(totalPrice)} (${count}/${MAX_SLOTS})`;
      }
    }
  }

  function renderThumbnails() {
    const emptyEl = document.getElementById('giftboxThumbsEmpty');
    const listEl = document.getElementById('giftboxThumbsList');
    if (!listEl) return;

    if (state.selectedItems.length === 0) {
      if (emptyEl) emptyEl.style.display = 'flex';
      listEl.innerHTML = '';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';

    // Show filled thumbs + empty slot placeholders
    const slots = Array.from({ length: MAX_SLOTS }, (_, i) => state.selectedItems[i] || null);

    listEl.innerHTML = slots.map((item, i) => {
      if (item) {
        return `<div class="gb-sticky-thumb" title="${escapeAttr(item.title)}">
          <img src="${item.image || ''}" alt="${escapeAttr(item.title)}" loading="lazy">
          <button class="gb-thumb-remove-btn" data-product-id="${item.productId}" aria-label="Remove">×</button>
        </div>`;
      } else {
        return `<div class="gb-sticky-thumb empty-slot" title="Slot ${i + 1}">
          <span>+</span>
        </div>`;
      }
    }).join('');

    listEl.querySelectorAll('.gb-thumb-remove-btn').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const productId = btn.dataset.productId;
        state.selectedItems = state.selectedItems.filter(i => i.productId != productId);
        const card = document.querySelector(`[data-product-id="${productId}"]`);
        if (card) card.classList.remove('selected');
        refreshSlotBadges();
        refreshMaxReachedState();
        updateStickyBar();
      });
    });
  }

  // ─── Mobile Keyboard Dismiss ──────────────────────────────────────────────
  function setupMobileKeyboardDismiss() {
    const searchInput = document.getElementById('giftboxSearchInput');
    if (!searchInput) return;
    let isSearchFocused = false;
    let scrollStartY = 0;
    searchInput.addEventListener('focus', () => { isSearchFocused = true; scrollStartY = window.scrollY; });
    searchInput.addEventListener('blur', () => { isSearchFocused = false; });
    window.addEventListener('scroll', () => {
      if (isSearchFocused && Math.abs(window.scrollY - scrollStartY) > 60) searchInput.blur();
    }, { passive: true });
  }

  // ─── Sticky Visibility ─────────────────────────────────────────────────────
  function observeStickyVisibility() {
    const bar = document.getElementById('giftboxStickyBar');
    if (!bar) return;
    const toggle = () => {
      const show = state.selectedItems.length > 0 || window.scrollY > 300;
      bar.classList.toggle('visible', show);
    };
    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
  }

  // ─── Add to Cart ───────────────────────────────────────────────────────────
  async function handleAddToCart() {
    if (state.selectedItems.length === 0) return;

    const btn = document.getElementById('giftboxCtaBtn');
    const loader = btn?.querySelector('.gb-sticky-cta-loader');
    const text = document.getElementById('giftboxCtaText');

    if (btn) btn.disabled = true;
    if (loader) loader.style.display = 'inline-block';
    if (text) text.textContent = 'Adding to cart…';

    const giftBoxId = `giftbox_${Date.now()}`;

    // If a fixed gift box price is set, we need to handle pricing at checkout
    // Items are added at their normal price; discount handled via discount code or script
    const items = state.selectedItems.map((item, idx) => ({
      id: item.variantId,
      quantity: 1,
     // NEW — visible properties (no underscore = visible)
properties: {
  'Gift Box ID': giftBoxId,
  'Slot': `${idx + 1} of 7`,
  'Type': 'Travel Spray Gift Box'
}
    }));

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Failed to add to cart');
      }

      showToast('🎁 Gift Box added to cart!', 'success');
      await refreshCartCount();
      setTimeout(() => { window.location.href = '/cart'; }, 1200);

    } catch (err) {
      console.error('Cart error:', err);
      showToast(err.message || 'Could not add to cart. Please try again.', 'error');
      if (btn) btn.disabled = false;
    } finally {
      if (loader) loader.style.display = 'none';
      updateStickyBar();
    }
  }

  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count], .cart-count').forEach(el => {
        el.textContent = cart.item_count;
        el.setAttribute('data-cart-count', cart.item_count);
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (e) {}
  }

  // ─── Event Listeners ───────────────────────────────────────────────────────
  function setupEventListeners() {
    const searchInput = document.getElementById('giftboxSearchInput');
    const searchClear = document.getElementById('giftboxSearchClear');

    searchInput?.addEventListener('input', e => {
      const val = e.target.value;
      if (searchClear) searchClear.style.display = val ? 'flex' : 'none';
      clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => {
        state.searchQuery = val;
        applyFilters();
      }, 280);
    });

    searchClear?.addEventListener('click', () => {
      if (searchInput) searchInput.value = '';
      searchClear.style.display = 'none';
      state.searchQuery = '';
      applyFilters();
    });

    document.querySelectorAll('.gb-filter-pill').forEach(pill => {
      pill.addEventListener('click', () => {
        document.querySelectorAll('.gb-filter-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        state.activeCollection = pill.dataset.collection || '';
        applyFilters();
      });
    });

    document.getElementById('giftboxCtaBtn')?.addEventListener('click', handleAddToCart);
  }

  // ─── Loading State ─────────────────────────────────────────────────────────
  function setLoading(loading) {
    state.isLoading = loading;
    const skeleton = document.getElementById('giftboxSkeletonGrid');
    const container = document.getElementById('giftboxProductsContainer');
    if (skeleton) skeleton.style.display = loading ? 'grid' : 'none';
    if (container) container.style.display = loading ? 'none' : '';
  }

  function showError(msg) {
    const container = document.getElementById('giftboxProductsContainer');
    if (container) container.innerHTML = `<div class="giftbox-empty"><p>${escapeHtml(msg)}</p></div>`;
  }

  // ─── Toast ────────────────────────────────────────────────────────────────
  function showToast(msg, type = 'info') {
    const toast = document.createElement('div');
    toast.className = `gb-toast gb-toast--${type}`;
    toast.textContent = msg;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('visible'));
    setTimeout(() => {
      toast.classList.remove('visible');
      setTimeout(() => toast.remove(), 400);
    }, 3500);
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function isExcludedValue(val) {
    const excluded = (config.excludedVariants || []).map(v => v.toLowerCase().trim()).filter(Boolean);
    if (!excluded.length || !val) return false;
    return excluded.some(ex => val.toLowerCase().includes(ex));
  }

  function getCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts > 15 * 60 * 1000) return null;
      return data;
    } catch (e) { return null; }
  }

  function setCache(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch (e) {}
  }

  function gbEl(id, text) {
    const e = document.getElementById(id);
    if (e) e.textContent = text;
  }

  function escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function escapeJson(obj) {
    return JSON.stringify(obj).replace(/'/g, '&#39;').replace(/"/g, '&quot;');
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();